// ============================================================================
// 1. 임포트 및 전역 설정 (Imports & Config) 
// ============================================================================
import React, { useEffect, useState, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  LineChart, Line, Legend, PieChart, Pie, Cell 
} from 'recharts';
import { 
  TrendingUp, Users, Target, History, Loader2, Search, X, Download,
  MessageSquare, Clock, Briefcase, Calendar, XCircle, MapPin, PieChart as PieChartIcon, 
  Trophy, ExternalLink, PieChart as ChartPie, ShieldCheck, Filter, BarChart3
} from 'lucide-react';
import { crmDataService } from '../services/crmDataService';

// // 👇 새로 만든 공용 모달 부품 가져오기
// import DealDetailModal from './DealDetailModal';

//지도 가져오기
import KoreaMap from './KoreaMap';

// 원형 차트 색상 (기존 유지)
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#64748b'];

// 지역별 파이 차트용 예쁜 색상
const PIE_COLORS = ['#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe', '#cbd5e1']; 

interface DashboardProps {
  globalRefreshKey?: number;
  onNavigate?: (pageName: string) => void; 
  currentTime?: string;
  currentUser?: any;
}

// 🚨 [추가] 하단 기간 필터용 타입 정의 (custom 추가)
type FeedFilterType = 'week' | 'month' | '3months' | 'custom';
// 🚨 [신규] 지도/피드 연동 팀 필터 타입
type TeamFilterType = '전체' | '국내팀' | 'CS팀';

const Dashboard: React.FC<DashboardProps> = ({ globalRefreshKey, onNavigate, currentTime, currentUser }) => {
  const [isLoading, setIsLoading] = useState(true);

  // [상단] KPI 상태
  const [kpi, setKpi] = useState({
    annual_target: 0,
    total_forecast: 0,
    achievement_rate: 0,
    confirmed_revenue: 0,
    yoy_growth: 0,
    achievement_count: 0, // activity_count를 명칭에 맞춰 유지
    activity_count: 0
  });
  
  // 🚨 [상단 전용 필터] (매출예정표/차트를 뽑아오는 기준)
  const [startDate, setStartDate] = useState<string>('2026-01-01');
  const [endDate, setEndDate] = useState<string>('2026-12-31');

  // 담당자 필터링을 위한 상태
  const [selectedAssignee, setSelectedAssignee] = useState<string>('전체');
  const [assigneeList, setAssigneeList] = useState<string[]>([]);

  // [중단] 차트 및 지역 데이터
  const [charts, setCharts] = useState({
    history: [] as any[],    
    pipeline: [] as any[],   
    regions: {} as Record<string, { count: number, amount: number }> 
  });

  const [chartMode, setChartMode] = useState<'monthly' | 'category'>('monthly');
  // 🚨 [신규] 파이프라인 차트 모달 팝업 상태
  const [showPipelineModal, setShowPipelineModal] = useState(false);

  // 3단 드릴다운을 위한 상태 모음
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null); 
  const [selectedClientForFeed, setSelectedClientForFeed] = useState<string | null>(null); 

  // [하단] 실시간 활동 로그를 위한 상태
  const [activityLogs, setActivityLogs] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // 🚨 현재 지도에 떠있는 거래처 목록 바구니 (하단 전용으로 전체 데이터를 담습니다)
  const [activeDealsList, setActiveDealsList] = useState<any[]>([]); 

  // 🚨 [하단 전용 필터] 피드(영업활동) 기간 필터
  const [feedFilter, setFeedFilter] = useState<FeedFilterType>('week'); 
  // 🚨 [하단 전용 달력 상태 추가 - 시스템 실행 당일 기본 포맷 고정 조립]
  const [feedStartDate, setFeedStartDate] = useState<string>(() => {
    const now = new Date();
    const day = now.getDay();
    const diffToMon = day === 0 ? -6 : 1 - day;
    const mon = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diffToMon);
    return mon.toISOString().split('T')[0]; // 기본값 이번 주 월요일 세팅
  });
  const [feedEndDate, setFeedEndDate] = useState<string>(() => {
    const now = new Date();
    const day = now.getDay();
    const diffToMon = day === 0 ? -6 : 1 - day;
    const sun = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diffToMon + 6);
    return sun.toISOString().split('T')[0]; // 기본값 이번 주 일요일 세팅
  });
  // 🚨 [신규] 지도 및 피드 연동 팀 필터
  const [mapTeamFilter, setMapTeamFilter] = useState<TeamFilterType>('전체');
  
  const [selectedDeal, setSelectedDeal] = useState<any>(null);

  //  피드 작성자 필터 상태 및 작성자 목록 자동 추출
  const [feedAssigneeFilter, setFeedAssigneeFilter] = useState<string>('전체');

  // 🚨 [신규 추가] 피드에서 '진짜 사람' 작성자만 추출하여 드롭다운 명단 만들기 (CS팀 꼬리표 제거)
  const feedEditors = useMemo(() => {
    const editors = new Set<string>();
    const autoStagePattern = /^단계를\s(미방문|방문|견적|제안서|사양서|조달접수|계약)로\s변경$/;
    
    activityLogs.forEach(log => {
      const content = (log.content || "").trim();
      const isFileLog = content.includes('[파일 첨부]');
      const isAutoStage = autoStagePattern.test(content);
      const isSystemAuto = content.includes('[시스템 자동기록]');
      
      if (!isFileLog && !isAutoStage && !isSystemAuto && log.editor) {
        // 🎯 [핵심] '[CS팀]' 글자를 지우고 순수 이름만 바구니에 담습니다. (중복 자동 제거됨)
        const cleanName = log.editor.replace('[CS팀]', '').trim();
        editors.add(cleanName);
      }
    });
    return Array.from(editors).sort();
  }, [activityLogs]);

  // G2B 수주 요약 데이터
  const [g2bSummary, setG2bSummary] = useState({
    totalCount: 0, 
    totalAmount: 0, 
    ourWinCount: 0, 
    ourWinAmount: 0, 
    regionStats: [] as any[]
  });

  // 시간 포맷터
  const formatExactTime = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  
  return `${y}.${m}.${d} ${h}:${min}`; // 예: 2026.03.25 14:30
};

  const handleNavigate = (page: string) => {
    if (onNavigate) {
      onNavigate(page);
    } else {
      alert(`'${page}' 탭으로 이동합니다! (현재는 화면 테스트 중)`);
    }
  };

  // ===========================================================================
  // 🚨 [핵심 동적 제어] 이번 주(월~일), 이번 달 시작일/종료일 자동 변경 계산 로직 고도화
  // ===========================================================================
  const handleFeedFilterChange = (filterType: FeedFilterType) => {
    setFeedFilter(filterType);
    
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const d = now.getDate();
    const day = now.getDay();

    const formatDateInput = (date: Date) => {
      return date.toISOString().split('T')[0];
    };

    if (filterType === 'week') {
      const diffToMon = day === 0 ? -6 : 1 - day;
      const mon = new Date(y, m, d + diffToMon);
      const sun = new Date(y, m, d + diffToMon + 6);
      setFeedStartDate(formatDateInput(mon));
      setFeedEndDate(formatDateInput(sun));
    } else if (filterType === 'month') {
      const firstDay = new Date(y, m, 1);
      const lastDay = new Date(y, m + 1, 0);
      setFeedStartDate(formatDateInput(firstDay));
      setFeedEndDate(formatDateInput(lastDay));
    } else if (filterType === '3months') {
      const past = new Date(y, m, d - 90);
      setFeedStartDate(formatDateInput(past));
      setFeedEndDate(formatDateInput(now));
    }
  };

  // ===========================================================================
  // 🚨 [핵심] 이번 주(월~금), 이번 달 자동 날짜 계산 + 커스텀 달력 로직
  // ===========================================================================
  const feedDateRange = useMemo(() => {
    const start = new Date(feedStartDate);
    const end = new Date(feedEndDate);
    
    const formatDate = (date: Date) => `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
    const getStartOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0).getTime();
    const getEndOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999).getTime();

    return {
      label: `${formatDate(start)} ~ ${formatDate(end)}`,
      startTime: getStartOfDay(start),
      endTime: getEndOfDay(end)
    };
  }, [feedStartDate, feedEndDate]);

  useEffect(() => {
    const loadAssigneeList = async () => {
      try {
        const response = await crmDataService.getSalesTargets();
        if (response.status === 'success' && response.data) {
          const targetNames = response.data.map((item: any) => item.manager.trim());
          const uniqueNames = Array.from(new Set(targetNames))
            .filter(name => name && name !== '미정') as string[];
          setAssigneeList(uniqueNames.sort());
        }
      } catch (error) {
        console.error("담당자 목록 로드 실패:", error);
      }
    };
    loadAssigneeList();
  }, []);

  // ===========================================================================
  // 🚨 1. [상단 전용 방] KPI, 차트, G2B (오직 상단 날짜/담당자 필터만 봅니다)
  // ===========================================================================
  useEffect(() => {
    const fetchTopData = async () => {
      setIsLoading(true);
      try {
        const [kpiData, chartData, g2bData] = await Promise.all([
          crmDataService.getDashboardKPI(startDate, endDate, selectedAssignee),
          crmDataService.getDashboardCharts(startDate, endDate, selectedAssignee),
          crmDataService.getG2BList()
        ]);

        setKpi(kpiData);
        setCharts(chartData);

        // G2B 수주 현황 집계
        if (g2bData && Array.isArray(g2bData)) {
          let totalWinAmt = 0;
          let ourWinAmt = 0;
          let ourWinCnt = 0;
          let totalCnt = 0;

          const regStatsMap: Record<string, { region: string, budget: number, ourWin: number, lost: number }> = {};
          
          g2bData.forEach((bid: any) => {
            const reg = bid.region || '미분류';
            if (!regStatsMap[reg]) regStatsMap[reg] = { region: reg, budget: 0, ourWin: 0, lost: 0 };
            
            const budget = Number(bid.budget) || 0;
            const winPrice = Number(bid.win_price) || 0;
            const status = String(bid.status || '');
            const memo = String(bid.memo || '').toLowerCase();
            const winCompany = String(bid.win_company || '');
            const isOurWin = status.includes('자사') || memo.includes('수주') || winCompany.includes('(주)아이지');

            regStatsMap[reg].budget += budget;
            
            if (status.includes('낙찰완료')) {
              totalCnt++;
              totalWinAmt += winPrice;
              if (isOurWin) {
                ourWinCnt++;
                ourWinAmt += winPrice;
                regStatsMap[reg].ourWin += winPrice;
              } else {
                regStatsMap[reg].lost += winPrice;
              }
            }
          });

          setG2bSummary({ 
            totalCount: totalCnt, 
            totalAmount: totalWinAmt, 
            ourWinCount: ourWinCnt, 
            ourWinAmount: ourWinAmt, 
            regionStats: Object.values(regStatsMap).sort((a, b) => b.budget - a.budget)
          });
        }
      } catch (error) { console.error(error); } 
      finally { setIsLoading(false); }
    };

    fetchTopData();
  }, [startDate, endDate, selectedAssignee, globalRefreshKey]); // 👈 상단 필터만 주시합니다.


  // ===========================================================================
  // 🚨 2. [하단 전용 방] 피드, 지도, 지역 목록 (상단 필터 무시! 서버에서 1번만 가져옴)
  // ===========================================================================
  useEffect(() => {
    const fetchBottomData = async () => {
      try {
        const [feedLogs, allDeals, allContacts] = await Promise.all([
          crmDataService.getDashboardActivityFeed(0), // 0을 넣어 모든 히스토리를 긁어옵니다.
          crmDataService.getDeals(), 
          crmDataService.getContacts() 
        ]);

        const dealMap = new Map();
        const regionMap = new Map();
        
        // 1. [매출예정표] 바구니에 담기 (과거/현재 묻지도 따지지도 않고 다 넣음)
        (allDeals || []).forEach((d: any) => {
          const rawId = String(d.id).trim();
          const cName = String(d['거래처'] || '').trim();
          const rawRegion = String(d['지역'] || '기타').replace(/\s+/g, '');
          const cRegion = rawRegion === '-' || rawRegion === '' ? '기타' : rawRegion;
          
          dealMap.set(rawId, cName);
          regionMap.set(rawId, cRegion);
          
          if (cName) regionMap.set(cName, cRegion); 
        });

        // 2. [contact_list(고객명단)] 바구니에 붓기
        (allContacts || []).forEach((c: any) => {
          const rawId = String(c.id).trim();
          const cName = String(c.company || c.company_name || c.거래처 || '').trim();
          
          const rawRegion = String(c.region || c.지역 || c.region_api || '기타').replace(/\s+/g, '');
          const cRegion = (rawRegion === '-' || rawRegion === '') ? '기타' : rawRegion;
          
          dealMap.set(rawId, cName);
          regionMap.set(rawId, cRegion);
          
          if (cName && (!regionMap.has(cName) || regionMap.get(cName) === '기타')) {
            regionMap.set(cName, cRegion);
          }
        });

        // 🚨 하단 전용 딜 목록 저장 (연도 상관없이 전체를 담아둡니다)
        setActiveDealsList(allDeals || []);

        // 3. [피드 가공 로직] 이중 매칭
        const processedLogs = (feedLogs || [])
          .filter((v: any, i: number, a: any[]) => 
            a.findIndex((t: any) => 
              t.content === v.content && 
              t.created_at === v.created_at && 
              t.editor === v.editor
            ) === i
          )
          .map((log: any) => {
            const rawDealId = String(log.deal_id || '').trim();
            const clientName = dealMap.get(rawDealId) || rawDealId || '거래처 미상';
            const region = regionMap.get(rawDealId) || regionMap.get(clientName) || '기타';

            return {
              ...log,
              client_name: clientName,
              region: region
            };
          });

        setActivityLogs(processedLogs);

      } catch (error) { console.error(error); } 
    };

    fetchBottomData();
  }, [globalRefreshKey]); // 👈 🚨 상단 날짜 필터(startDate)가 빠져있습니다! 오로지 새로고침 키만 봅니다.


  // ===========================================================================
  // 🚨 [데이터 가공] 프론트의 "실시간 피드(activityLogs)" 기반으로 지도 데이터 덮어쓰기!
  // ===========================================================================
  const regionChartData = useMemo(() => {
    // 🎯 [수정] 팀별로 카운트를 따로 담을 수 있게 구조 변경
    const counts: Record<string, { koreaTeam: number, csTeam: number }> = {};
    const validRegions = ["서울", "경기", "인천", "강원", "충북", "충남", "대전", "세종", "전북", "전남", "광주", "경북", "경남", "대구", "부산", "울산", "제주"];
    
    // 초기화
    validRegions.forEach(reg => { counts[reg] = { koreaTeam: 0, csTeam: 0 }; });

    activityLogs.forEach(log => {
      const logTime = new Date(log.created_at).getTime();
      
      if (logTime >= feedDateRange.startTime && logTime <= feedDateRange.endTime) {
        const content = (log.content || "").trim();
        const editor = log.editor || "";

        // 🚨 [핵심 필터] 딜보드에서 카드를 옮길 때 생기는 자동 로그 패턴 차단
        const autoStagePattern = /^단계를\s(미방문|방문|견적|제안서|사양서|조달접수|계약)로\s변경$/;
        const isSystemAuto = content.includes('[시스템 자동기록]');
        const isFileLog = content.includes('[파일 첨부]');

        if (autoStagePattern.test(content) || isSystemAuto || isFileLog) return;

        // 지역 추출
        const rawRegion = String(log.region || '기타').replace(/\s+/g, '');
        const cleanRegion = rawRegion.substring(0, 2); 
        
        if (validRegions.includes(cleanRegion)) {
            const isCS = editor.includes('[CS팀]');
            // 🎯 [신규] CS팀이면 csTeam 증가, 아니면 koreaTeam 증가
            if (isCS) counts[cleanRegion].csTeam += 1;
            else counts[cleanRegion].koreaTeam += 1;
        }
      }
    });

    // 배열로 변환해서 넘기기 (value는 총합으로 남겨둠)
    return Object.entries(counts)
      .map(([name, data]) => ({ 
        name, 
        koreaTeam: data.koreaTeam, 
        csTeam: data.csTeam,
        value: data.koreaTeam + data.csTeam, // 기존 로직 에러 방지용 총합
        amount: 0 
      }))
      .filter(item => item.value > 0) // 활동이 있는 지역만 살림
      .sort((a, b) => b.value - a.value); 
      
  }, [activityLogs, feedDateRange]); // 👈 🚨 mapTeamFilter 의존성 제거 (항상 둘 다 계산해서 넘김)

  const totalRegionCount = regionChartData.reduce((acc, curr) => acc + curr.value, 0);

  // 🚨 [수정] 지도를 선택했을 때만 거래처 목록을 보여주도록 필터링 로직 수정 (가짜 활동 제외)
  const activeClientList = useMemo(() => {
    if (!selectedRegion) return []; 
    
    const selRegShort = selectedRegion.substring(0, 2);
    const clientMap: Record<string, { name: string, count: number, lastDate: string | null }> = {};

    // 🚨 [필터 조건] 자동 생성 로그를 정확히 잡아내는 리스트 (사람이 쓴 글 보호)
    const AUTO_STAGES = ['미방문', '방문', '견적', '제안서', '사양서', '조달접수', '계약'];

    // 무조건 '활동 로그(activityLogs)'에 있는 놈들을 다 띄워줍니다!
    activityLogs.forEach(log => {
      const logTime = new Date(log.created_at).getTime();
      const logRegShort = (log.region || '').substring(0, 2);

      // 이번 주/이번 달 피드 중에서 + 방금 클릭한 지역(예: 강원)과 일치하는 놈만
      if (logTime >= feedDateRange.startTime && logTime <= feedDateRange.endTime && logRegShort === selRegShort) {
        
        const content = (log.content || "").trim();
        const editor = log.editor || "";

        // 🎯 [신규] 지도 연동 팀 필터 적용
        const isCS = editor.includes('[CS팀]');
        if (mapTeamFilter === 'CS팀' && !isCS) return;
        if (mapTeamFilter === '국내팀' && isCS) return;

        // 🚨 [핵심 필터] "단계를 OO로 변경" 과 완벽히 똑같거나, "[시스템 자동기록]" 인 경우 카운트 제외!
        const isAutoLog = AUTO_STAGES.some(stage => content === `단계를 ${stage}로 변경`);
        const isSystemAuto = content.includes('[시스템 자동기록]');
        const isFileLog = content.includes('[파일 첨부]');

        // 가짜(자동) 활동이면 무시하고 다음 로그로 넘어감 (목록에 안 담김)
        if (isAutoLog || isSystemAuto || isFileLog) return;

        const cName = (log.client_name || '거래처 미상').trim();
        
        if (!clientMap[cName]) {
          clientMap[cName] = { name: cName, count: 0, lastDate: null };
        }
        
        clientMap[cName].count += 1; // 진짜 활동 횟수 누적
        
        if (!clientMap[cName].lastDate || logTime > new Date(clientMap[cName].lastDate!).getTime()) {
          clientMap[cName].lastDate = log.created_at;
        }
      }
    });

    // 진짜 활동이 1건이라도 있는 업체만 많은 순서대로 정렬해서 렌더링
    return Object.values(clientMap).sort((a, b) => b.count - a.count);
  }, [activityLogs, selectedRegion, feedDateRange, mapTeamFilter]);

  // 🚨 [핵심 수정] 하단 우측 상세 피드 필터링 로직 (선택 안됐을 땐 전체 노출)
  const filteredLogs = useMemo(() => {
    return activityLogs.filter(log => {
      const content = (log.content || "").trim();
      const editor = (log.editor || "").trim();

      // -----------------------------------------------------------
      // [A] 불필요한 자동 로그 차단 (파일 첨부 & 단계 변경)
      // -----------------------------------------------------------
      if (content.includes('[파일 첨부]')) return false; 
      
      const autoStagePattern = /^단계를\s(미방문|방문|견적|제안서|사양서|조달접수|계약)로\s변경$/;
      const isAutoLog = autoStagePattern.test(content);
      const isSystemAuto = content.includes('[시스템 자동기록]');
      if (isAutoLog || isSystemAuto) return false; 

      // -----------------------------------------------------------
      // [B] 🎯 팀 필터 적용 (지도 탭과 동기화)
      // -----------------------------------------------------------
      const isCS = editor.includes('[CS팀]');
      if (mapTeamFilter === 'CS팀' && !isCS) return false;
      if (mapTeamFilter === '국내팀' && isCS) return false;

      // -----------------------------------------------------------
      // [C] 작성자 드롭다운 필터 적용 (순수 이름으로 비교)
      // -----------------------------------------------------------
      const cleanEditor = editor.replace('[CS팀]', '').trim(); 
      if (feedAssigneeFilter !== '전체' && cleanEditor !== feedAssigneeFilter) {
        return false;
      }

      // 1. 날짜 범위 필터
      const logTime = new Date(log.created_at).getTime();
      if (logTime < feedDateRange.startTime || logTime > feedDateRange.endTime) return false;

      // 2. 검색어 필터
      const searchTarget = `${log.client_name} ${log.editor} ${log.content}`.toLowerCase();
      if (searchQuery && !searchTarget.includes(searchQuery.toLowerCase())) return false;

      // 3. 📍 3단계 지능형 축소 필터
      
      // [A] 거래처 선택됨 -> 해당 거래처명 매칭
      if (selectedClientForFeed) {
        return log.client_name === selectedClientForFeed;
      }

      // [B] 지역만 선택됨 -> 로그의 지역 정보가 없더라도 '거래처 이름'으로 지역 판별
      if (selectedRegion) {
        const selRegShort = selectedRegion.substring(0, 2);
        const logRegShort = (log.region || '').substring(0, 2);
        if (logRegShort === selRegShort) return true;
        return false;
      }

      // [C] 아무것도 선택 안 함 -> 전체 노출
      return true;
    }).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [activityLogs, searchQuery, selectedRegion, selectedClientForFeed, feedDateRange, feedAssigneeFilter, mapTeamFilter]);

  const categoryKeys = useMemo(() => {
    const keys = new Set<string>();
    (charts.pipeline || []).forEach(data => {
      Object.keys(data).forEach(k => {
        if (!['Month', '실적', '확정', '예정', '미정'].includes(k)) {
          keys.add(k);
        }
      });
    });
    return Array.from(keys);
  }, [charts.pipeline]);

  const openDealHistory = (dealId: string, dealInfo: any) => {
    if (!dealInfo) return;
    setSelectedDeal(dealInfo); 
  };

  const formatKRW = (value: number) => new Intl.NumberFormat('ko-KR').format(value);

  const formatCompact = (num: number) => {
    if (num >= 100000000) return `${(num / 100000000).toFixed(1).replace('.0', '')}억`;
    if (num >= 10000) return `${(num / 10000).toFixed(0)}만`;
    return num.toLocaleString();
  };

  const totalActivityCountHeader = useMemo(() => {
  if (!selectedRegion) {
    // 1. 아무 지역도 안 눌렀을 때 -> 전국 진짜 활동 총합
    return totalRegionCount; 
  }
  
  if (selectedClientForFeed) {
    // 2. 특정 거래처(예: 충남교육청)를 눌렀을 때 -> 그 거래처의 진짜 활동 개수(2건)
    const client = activeClientList.find(c => c.name === selectedClientForFeed);
    return client ? client.count : 0;
  }
  
  // 3. 지역(예: 충남)만 눌렀을 때 -> 해당 지역 거래처들의 진짜 활동 개수 합산(3건)
  return activeClientList.reduce((sum, c) => sum + c.count, 0);
}, [selectedRegion, selectedClientForFeed, activeClientList, totalRegionCount]);

  return (
    <div className="space-y-6 animate-fade-in pb-10 font-sans">
        
      {/* ========================================================================= */}
      {/* 상단 1단: 필터 및 타이틀 */}
      {/* ========================================================================= */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-2">
        <div className="flex flex-col gap-2 self-start md:self-center">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-black text-slate-900 tracking-tight">실적 분석 대시보드</h1>
            {currentTime && (
              <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500 bg-white px-2.5 py-1 rounded-full border border-slate-200 shadow-sm">
                 <Clock size={12} className="text-blue-500" />
                 {currentTime}
              </div>
            )}
          </div>
          {/* 🚨 [신규] 차트 열기 버튼을 제목 바로 아래에 배치하여 접근성 높임 */}
          <button 
            onClick={() => setShowPipelineModal(true)}
            className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-xl text-xs font-bold shadow-md hover:bg-indigo-700 transition-all w-max hover:-translate-y-0.5"
          >
            <BarChart3 size={14} /> 📊 월별 매출 파이프라인 열기
          </button>
        </div>

        <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-black text-slate-400">담당자</span>
            <select 
              value={selectedAssignee}
              onChange={(e) => setSelectedAssignee(e.target.value)}
              className="bg-transparent text-[12px] font-black text-blue-600 outline-none cursor-pointer"
            >
              <option value="전체">전체</option>
              {assigneeList.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <div className="w-[1px] h-4 bg-slate-200" />
          <div className="flex items-center gap-1">
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-transparent text-[11px] font-black text-slate-600 outline-none cursor-pointer" />
            <span className="text-slate-300">~</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-transparent text-[11px] font-black text-slate-600 outline-none cursor-pointer" />
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 상단 2단: 4개의 KPI 카드 (크기/구조 완벽 유지) */}
      {/* ========================================================================= */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 shrink-0">
        <div onClick={() => handleNavigate('sales_target')} className="bg-white p-5 rounded-[24px] shadow-sm border border-slate-100 cursor-pointer transition-all hover:-translate-y-1 hover:border-blue-300 group">
            <p className="text-[10px] font-black text-slate-400 uppercase mb-1">TOTAL Target</p>
            <h3 className="text-xl font-black text-slate-900 font-mono">₩{formatKRW(kpi.annual_target)}</h3>
            <div className="mt-4 flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
                <Target size={14} className="text-blue-600" /> <span>매출 목표</span>
            </div>
        </div>
        <div onClick={() => handleNavigate('forecast')} className="bg-white p-5 rounded-[24px] shadow-sm border border-slate-100 cursor-pointer transition-all hover:-translate-y-1 hover:border-indigo-300 group">
          <p className="text-[10px] font-black text-slate-400 uppercase mb-1">Total Forecast</p>
          <h3 className="text-xl font-black text-indigo-700 font-mono">₩{formatKRW(kpi.total_forecast)}</h3>
          <div className="mt-4 flex items-center gap-1.5 text-[11px] font-bold text-indigo-600">
            <TrendingUp size={14} /> <span>매출 예상액</span>
          </div>
        </div>
        <div onClick={() => handleNavigate('forecast_detail')} className="bg-white p-5 rounded-[24px] shadow-sm border border-slate-100 cursor-pointer transition-all hover:-translate-y-1 hover:border-green-300 group">
            <p className="text-[10px] font-black text-slate-400 uppercase mb-1">Achievement</p>
            <h3 className="text-xl font-black text-green-600 font-mono">₩{formatKRW(kpi.confirmed_revenue)}</h3>
            <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-md">
                <TrendingUp size={12} /> 달성률 {kpi.achievement_rate.toFixed(1)}%
            </div>
        </div>
        <div onClick={() => handleNavigate('pipeline')} className="bg-white p-5 rounded-[24px] shadow-sm border border-slate-100 cursor-pointer transition-all hover:-translate-y-1 hover:border-purple-300 group">
            <p className="text-[10px] font-black text-slate-400 uppercase mb-1">Total Activities</p>
            <h3 className="text-xl font-black text-slate-900 font-mono">{kpi.activity_count}건</h3>
            <div className="mt-4 flex items-center gap-1.5 text-[11px] font-bold text-purple-600">
                <Users size={14} /> <span>진행 건수</span>
            </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 🚨 [최종 고도화] 하단 트래킹 헤더 기간 필터 자동 연동 + 주간 일지 엑셀 추출 마스터 바 */}
      {/* ========================================================================= */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mt-6 mb-4 p-4 bg-white rounded-3xl border border-slate-200 shadow-sm gap-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-black text-slate-800 flex items-center gap-2">
            <span className="p-1.5 bg-blue-600 text-white rounded-lg"><Clock size={16} strokeWidth={3}/></span>
            영업 활동 트래킹
          </h3>
          <span className="text-[12px] font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-full border border-blue-100 shadow-sm font-mono">
            조회 범위: {feedDateRange.label}
          </span>
        </div>
        
        <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto justify-between lg:justify-end">
          {/* 1. 동적 연동형 기간 버튼 레일 */}
          <div className="flex gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200">
            <button onClick={() => handleFeedFilterChange('week')} className={`px-4 py-1.5 rounded-lg text-[11px] font-black transition-all ${feedFilter === 'week' ? 'bg-white text-blue-600 shadow-sm border border-slate-200/40' : 'text-slate-500 hover:bg-slate-50'}`}>이번 주</button>
            <button onClick={() => handleFeedFilterChange('month')} className={`px-4 py-1.5 rounded-lg text-[11px] font-black transition-all ${feedFilter === 'month' ? 'bg-white text-blue-600 shadow-sm border border-slate-200/40' : 'text-slate-500 hover:bg-slate-50'}`}>이번 달</button>
            <button onClick={() => handleFeedFilterChange('3months')} className={`px-4 py-1.5 rounded-lg text-[11px] font-black transition-all ${feedFilter === '3months' ? 'bg-white text-blue-600 shadow-sm border border-slate-200/40' : 'text-slate-500 hover:bg-slate-50'}`}>최근 3개월</button>
          </div>
          
          {/* 2. 동기화 반영 수동 달력 인풋 창 */}
          <div className={`flex items-center gap-1 bg-slate-50 px-3 py-1.5 rounded-xl border shadow-sm transition-all ${feedFilter === 'custom' ? 'border-blue-400 ring-4 ring-blue-50 bg-white' : 'border-slate-200'}`}>
            <input 
              type="date" 
              value={feedStartDate} 
              onChange={e => { setFeedStartDate(e.target.value); setFeedFilter('custom'); }} 
              className="bg-transparent text-[11px] font-black text-slate-600 outline-none cursor-pointer font-mono" 
            />
            <span className="text-slate-300 text-[10px] font-bold">~</span>
            <input 
              type="date" 
              value={feedEndDate} 
              onChange={e => { setFeedEndDate(e.target.value); setFeedFilter('custom'); }} 
              className="bg-transparent text-[11px] font-black text-slate-600 outline-none cursor-pointer font-mono" 
            />
          </div>

          {/* 3. 🚀 [요구사항 구현] 주간 업무 일지 7대 항목 원천 매칭 엑셀 추출 엔진 */}
          <button
            onClick={() => {
              if (filteredLogs.length === 0) {
                alert("현재 필터링된 통합 조건(기간/팀/작성자)의 영업 활동 데이터가 없습니다.");
                return;
              }
              
              // 1. 🚨 [기획 반영] 담당자를 맨 앞으로 보내고 '상담내용'을 '특이사항'으로 변경한 보고서 표준 8대 헤더
              const headers = ["담당자", "방문일자", "지역", "거래처", "거래처 담당자", "연락처", "예산", "상담내용"];
              
              // 2. 재배치된 헤더 순서에 맞춰 1:1 정밀 매칭 조립
              const rows = filteredLogs.map(log => {
                // 담당자(작성자) 이름 정제 (CS팀 꼬리표 떼고 순수 이름만)
                const pureEditor = log.editor ? String(log.editor).replace('[CS팀]', '').trim() : "-";

                // 방문일 정제
                const rawDate = log.visit_date || log.visitDate;
                const finalVisitDate = rawDate && String(rawDate).trim() !== "" 
                  ? String(rawDate).trim() 
                  : (log.created_at ? String(log.created_at).substring(0, 10) : "-");

                // 특이사항(상담내용) 정제 및 엑셀 포맷 파괴 방지 쌍따옴표 래핑
                const pureContent = log.content ? String(log.content).replace(/\[.*?\]/g, '').trim() : "-";
                
                return [
                  pureEditor,                                   // 1열: 담당자 (제일 앞으로 전진 👤)
                  finalVisitDate,                               // 2열: 방문일자
                  log.region || "-",                            // 3열: 지역
                  log.client_name || "-",                       // 4열: 거래처
                  log.contact_person || log.contactName || "-", // 5열: 거래처 담당자
                  log.phone || "-",                             // 6열: 연락처
                  log.budget ? `${String(log.budget).replace(/,/g, '')}` : "-", // 7열: 예산
                  pureContent                                   // 8열: 특이사항
                ];
              });

              // 3. XLSX 라이브러리를 사용하여 진짜 엑셀 파일 생성
              const workbook = XLSX.utils.book_new();
              const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
              XLSX.utils.book_append_sheet(workbook, worksheet, "영업활동기록");
              
              const cleanFilterName = feedAssigneeFilter === "전체" ? "전체직원" : feedAssigneeFilter;
              const fileName = `주간업무일지_${cleanFilterName}_(${feedStartDate}~${feedEndDate}).xlsx`;
              
              XLSX.writeFile(workbook, fileName);
            }}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black transition-all shadow-md active:scale-95 hover:-translate-y-0.5"
          >
            <Download size={14} strokeWidth={3} /> 주간 일지용 엑셀 다운로드
          </button>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 🚨 3단 메인 그리드 (무한정 스크롤 늘어남 버그 100% 픽스!) 
          전체 높이를 고정(xl:h-[850px])하고 내부에서 min-h-0로 잡아줍니다. */}
      {/* ========================================================================= */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 xl:h-[850px] mb-6">
        
        {/* ① 좌측 (세로 전체 차지): 영업 지도 영역 */}
        <div className="bg-gradient-to-b from-blue-50/60 to-white p-5 rounded-[32px] shadow-md border border-blue-100 h-[500px] xl:h-full flex flex-col min-h-0 relative overflow-hidden">
          <div className="flex flex-col gap-2 mb-4 shrink-0 z-10">
            <div className="flex justify-between items-center">
              <h4 className="text-[14px] font-black text-slate-900 flex items-center gap-1.5">
                <MapPin size={16} className="text-blue-500" /> 지역별 영업 지도
              </h4>
              {selectedRegion && (
                <button onClick={() => { setSelectedRegion(null); setSelectedClientForFeed(null); }} className="text-[10px] font-bold text-red-500 bg-red-50 px-2 py-1 rounded-md hover:bg-red-100 transition shadow-sm border border-red-100 flex items-center gap-1">
                  <X size={12}/> 필터 해제
                </button>
              )}
            </div>
            
            {/* 🚨 [팀 필터] 지도, 거래처, 피드를 동시에 필터링! */}
            <div className="flex gap-1 bg-white/80 p-1 rounded-xl border border-blue-200 w-max shadow-sm mt-1">
              {(['전체', '국내팀', 'CS팀'] as TeamFilterType[]).map(team => (
                <button 
                  key={team} 
                  onClick={() => {
                    setMapTeamFilter(team);
                    setFeedAssigneeFilter('전체'); // 팀이 바뀌면 드롭다운은 리셋
                  }}
                  className={`px-3 py-1 rounded-lg text-[11px] font-black transition-all ${mapTeamFilter === team ? 'bg-blue-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'}`}
                >
                  {team}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 min-h-0 bg-white/60 backdrop-blur-sm rounded-2xl flex flex-col items-center justify-center overflow-visible border border-white shadow-sm">
            <KoreaMap 
              regionData={regionChartData} 
              selectedRegion={selectedRegion} 
              onSelectRegion={(reg) => { setSelectedRegion(reg); setSelectedClientForFeed(null); }}
              mapTeamFilter={mapTeamFilter} // 👈 🚨 이 줄을 꼭 추가해 주세요!
            />
          </div>
        </div>

        {/* ② 중앙 (위/아래 절반씩): 거래처 현황 리스트 + 실시간 피드 리스트 */}
        {/* 부모에 min-h-0를 줘서 안쪽 리스트가 부모 크기를 못 넘어가게 강제! */}
        <div className="xl:col-span-1 flex flex-col gap-6 h-[850px] xl:h-full min-h-0">
          
          {/* 중앙 [상단]: 지역별 영업 현황 목록 */}
          <div className="bg-gradient-to-b from-indigo-50/60 to-white p-5 rounded-[32px] shadow-md border border-indigo-100 flex-1 flex flex-col min-h-0">
            <div className="px-1 py-0.5 flex items-center justify-between mb-4 shrink-0">
              <h4 className="text-[14px] font-black text-slate-800 flex items-center gap-1.5">
                <Briefcase size={16} className="text-indigo-500"/> 지역별 거래처 현황
              </h4>
              <span className="text-[10px] font-black text-indigo-500 bg-indigo-100 px-2 py-0.5 rounded-full border border-indigo-200">총 활동 {totalActivityCountHeader}건</span>
            </div>
            
            <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 space-y-2">
              {!selectedRegion ? (
                <div className="h-full flex flex-col items-center justify-center text-slate-400 opacity-60">
                  <MapPin size={32} className="mb-2" />
                  <p className="text-[11px] font-bold text-center">좌측 지도에서 지역을 선택하면<br/>거래처 목록이 나타납니다.</p>
                </div>
              ) : activeClientList.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-slate-400">
                  <p className="text-[11px] font-bold">해당 조건의 진행 건이 없습니다.</p>
                </div>
              ) : (
                activeClientList.map((client, idx) => (
                  <div 
                    key={idx} 
                    onClick={() => setSelectedClientForFeed(client.name === selectedClientForFeed ? null : client.name)}
                    className={`flex items-center justify-between p-3 rounded-2xl border transition-all cursor-pointer ${
                      selectedClientForFeed === client.name 
                      ? 'bg-indigo-600 text-white shadow-md border-indigo-500' 
                      : client.count === 0 
                        ? 'bg-slate-50/80 border-slate-200 opacity-70' 
                        : 'bg-white border-indigo-100 hover:border-indigo-300 hover:shadow-sm' 
                    }`}
                  >
                    <div className="flex flex-col min-w-0">
                      <span className={`text-[12px] font-[1000] truncate transition-colors ${
                        selectedClientForFeed === client.name ? 'text-white' : client.count === 0 ? 'text-slate-400' : 'text-slate-700'
                      }`}>
                        {client.name}
                      </span>
                      <span className={`text-[9px] font-bold transition-colors ${
                        selectedClientForFeed === client.name ? 'text-indigo-200' : 'text-slate-400'
                      }`}>
                        {client.count > 0 ? `최근: ${formatExactTime(client.lastDate!)}` : '최근 활동 없음 ⚠️'}
                      </span>
                    </div>
                    <span className={`text-[11px] font-black px-2 py-1 rounded-lg transition-colors ${
                      selectedClientForFeed === client.name ? 'bg-indigo-500 text-white border border-indigo-400' 
                      : client.count === 0 ? 'bg-slate-200 text-slate-400 border border-slate-300' 
                      : 'bg-indigo-50 text-indigo-600 border border-indigo-100' 
                    }`}>
                      {client.count}건
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* 중앙 [하단]: 실시간 영업 피드 */}
          <div className="bg-gradient-to-b from-emerald-50/60 to-white p-5 rounded-[32px] shadow-md border border-emerald-100 flex-1 flex flex-col min-h-0">
            {currentUser?.role === 'admin' || currentUser?.permissions?.show_dashboard_feed !== false ? (
              <>
                <div className="flex flex-col gap-3 mb-3 shrink-0">
                  <div className="flex items-center justify-between">
                    <h4 className="text-[14px] font-black text-slate-900 flex items-center gap-1.5">
                      <div className="p-1 bg-emerald-100 rounded-md text-emerald-600"><MessageSquare size={14} strokeWidth={3} /></div>
                      {selectedClientForFeed ? `${selectedClientForFeed} 히스토리` : "실시간 영업 피드"}
                    </h4>
                    <div className="flex gap-1 items-center">
                      {(selectedClientForFeed || selectedRegion) && (
                        <button 
                          onClick={() => {
                              setSelectedClientForFeed(null);
                              if (!selectedClientForFeed) setSelectedRegion(null); 
                          }}
                          className="text-[10px] font-black text-slate-400 hover:text-rose-500 mr-2 border-b border-transparent hover:border-rose-300 transition-all"
                        >
                          {selectedClientForFeed ? "목록전체" : "전체피드"}
                        </button>
                      )}
                      {selectedRegion && <span className="text-[9px] font-black text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200 shadow-sm">📍{selectedRegion}</span>}
                    </div>
                  </div>
                  
                  {/* 작성자 드롭다운 & 검색창 */}
                  <div className="flex items-center gap-2 w-full">
                    <select
                      value={feedAssigneeFilter}
                      onChange={(e) => setFeedAssigneeFilter(e.target.value)}
                      className="bg-white border border-emerald-200 rounded-lg text-[11px] font-bold text-emerald-700 outline-none px-2 py-1.5 shadow-sm cursor-pointer min-w-[90px]"
                    >
                      <option value="전체">전체 작성자</option>
                      {feedEditors.map(editor => (
                        <option key={editor} value={editor}>{editor}</option>
                      ))}
                    </select>

                    <div className="relative group flex-1">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-500 transition-colors" size={12} strokeWidth={3} />
                      <input 
                        type="text" placeholder="피드 내용 검색..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-7 pr-6 py-1.5 bg-white border border-emerald-100 rounded-lg text-[10px] font-bold outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-50 transition-all shadow-sm"
                      />
                      {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-300 hover:text-red-500"><X size={12} strokeWidth={3} /></button>}
                    </div>
                  </div>
                </div>
                
                {/* 스크롤이 무한정 늘어나는 것을 막는 핵심 flex-1 & min-h-0 & overflow */}
                <div className="flex-1 overflow-y-auto pr-1 custom-scrollbar space-y-2">
                  {filteredLogs.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-slate-400">
                      <p className="text-[11px] font-bold">
                        {selectedClientForFeed ? "등록된 상담 기록이 없습니다." : "표시할 피드가 없습니다."}
                      </p>
                    </div>
                  ) : (
                    filteredLogs.map((log, i) => (
                      <div 
                        key={i} 
                        onClick={() => {
                          sessionStorage.setItem('targetClientSearch', log.client_name);
                          handleNavigate('companies'); 
                        }} 
                        className="flex gap-2 p-3 rounded-2xl bg-white border border-slate-200 shadow-sm hover:border-emerald-300 hover:bg-emerald-50/30 cursor-pointer transition-all animate-fade-in group"
                        title={`${log.client_name} 거래처 화면으로 이동합니다`}
                      >
                        <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center font-black text-[11px] text-slate-400 shrink-0 group-hover:bg-emerald-500 group-hover:text-white transition-all shadow-inner">
                          {log.editor?.charAt(0) || 'U'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-center mb-1">
                            <div className="flex items-center gap-1.5 truncate">
                              <span className="font-black text-[9px] text-emerald-700 bg-emerald-50 px-1 py-0.5 rounded border border-emerald-200 truncate max-w-[70px]">
                                <Briefcase size={8} className="inline mr-0.5 mb-0.5"/>{log.client_name}
                              </span>
                              <span className="font-black text-[10px] text-slate-800 shrink-0">{log.editor}</span>
                            </div>
                            <span className="text-[9px] font-bold text-slate-400 shrink-0">{formatExactTime(log.created_at)}</span>
                          </div>

                          {/* 🚨 [실제 방문일/예산/주소록 연동 마스터 뱃지 탑재] */}
                          <div className="flex flex-wrap items-center gap-1.5 mb-2 text-[9px] font-black font-mono">
                            <span className="inline-flex items-center gap-0.5 bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                              📅 방문일: {log.visit_date || (log.created_at ? String(log.created_at).substring(0, 10) : "-")}
                            </span>
                            
                            {log.budget && String(log.budget).trim() !== "" && String(log.budget).trim() !== "-" && (
                              <span className="inline-flex items-center gap-0.5 bg-blue-50 text-blue-600 border border-blue-100 px-1.5 py-0.5 rounded shadow-sm">
                                💰 예산: {log.budget}
                              </span>
                            )}

                            {log.contact_person && log.contact_person !== "-" && (
                              <span className="inline-flex items-center gap-0.5 bg-emerald-50 text-emerald-700 border border-emerald-100 px-1.5 py-0.5 rounded">
                                👤 담당: {log.contact_person}
                              </span>
                            )}

                            {log.phone && log.phone !== "-" && (
                              <span className="inline-flex items-center gap-0.5 bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                                📞 {log.phone}
                              </span>
                            )}
                          </div>

                          <div className="text-[10px] text-slate-600 font-medium leading-relaxed whitespace-pre-wrap break-words w-full">
                            {log.content?.match(/\[([SABC]급)\]/) && <span className="inline-block bg-indigo-100 text-indigo-700 text-[8px] font-black px-1 rounded mr-1">{log.content?.match(/\[([SABC]급)\]/)?.[1]}</span>}
                            {log.content?.replace(/\[.*?\]/g, '').trim()}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : (
              <div className="bg-slate-50 p-5 rounded-[32px] shadow-sm border-2 border-dashed border-slate-200 h-full flex flex-col items-center justify-center text-slate-400 animate-fade-in">
                <ShieldCheck size={50} strokeWidth={1.5} className="mb-4 text-slate-300" />
                <h4 className="text-[15px] font-black text-slate-700 mb-1">실시간 영업 피드 접근 제한</h4>
                <p className="text-[11px] font-bold text-slate-400 text-center leading-relaxed">
                  보안 정책에 따라 해당 데이터의<br />열람 권한이 제한되었습니다.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ③ 우측 (세로 전체 차지): G2B 수주 요약 */}
        <div className="xl:col-span-1 bg-white p-6 rounded-[32px] shadow-sm border border-slate-100 flex flex-col h-[500px] xl:h-full min-h-0 overflow-hidden font-sans">
           <div className="flex justify-between items-center mb-4 shrink-0">
              <h4 className="text-[15px] font-black text-slate-800 flex items-center gap-2">
                 <div className="bg-indigo-600 p-1.5 rounded-lg text-white shadow-sm"><ChartPie size={14} /></div>
                 지역별 배정예산 및 수주현황
              </h4>
              <span onClick={() => handleNavigate('g2b')} className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 cursor-pointer transition-colors bg-indigo-50 px-2 py-1 rounded-md">상세 보기 &rarr;</span>
           </div>

           <div className="flex-1 border border-slate-100 rounded-xl overflow-hidden flex flex-col bg-white shadow-inner min-h-0">
             <div className="bg-slate-50 px-3 py-3 border-b border-slate-200 grid grid-cols-12 gap-0 text-[10px] font-black text-slate-500 text-center shrink-0">
                <div className="col-span-2 text-left">지역</div>
                <div className="col-span-3">전체배정</div>
                <div className="col-span-3 text-blue-600">아이지수주</div>
                <div className="col-span-2 text-rose-500">타사낙찰</div>
                <div className="col-span-2 text-right pr-2">점유율</div>
             </div>
             
             <div className="flex-1 overflow-y-auto custom-scrollbar bg-white" style={{ scrollbarGutter: 'stable' }}>
                {g2bSummary.regionStats.length > 0 ? (
                <div className="divide-y divide-slate-100">
                   {g2bSummary.regionStats.map((stat, idx) => {
                      const totalWinInRegion = stat.ourWin + stat.lost;
                      const winRatePercent = totalWinInRegion > 0 ? (stat.ourWin / totalWinInRegion) * 100 : 0;
                      return (
                      <div key={idx} className="grid grid-cols-12 gap-0 px-3 py-3 transition-colors items-center text-[11px] hover:bg-slate-50 text-center">
                         <div className="col-span-2 text-left font-black text-slate-800 truncate">{stat.region}</div>
                         <div className="col-span-3 font-bold text-slate-600">₩{formatCompact(stat.budget)}</div>
                         <div className="col-span-3 font-black text-blue-600">₩{formatCompact(stat.ourWin)}</div>
                         <div className="col-span-2 font-bold text-rose-400">₩{formatCompact(stat.lost)}</div>
                         <div className="col-span-2 text-right font-black text-slate-800 pr-2">
                             {winRatePercent > 0 ? <span className="bg-blue-50 text-blue-700 px-1 py-0.5 rounded text-[10px]">{winRatePercent.toFixed(0)}%</span> : '-'}
                         </div>
                      </div>
                      );
                   })}
                </div>
                ) : (
                <div className="h-full flex flex-col items-center justify-center text-slate-400">
                   <ChartPie size={40} className="mb-2 opacity-20" /><p className="text-[12px] font-bold">집계된 데이터가 없습니다.</p>
                </div>
                )}
             </div>
             
             <div className="bg-slate-100 px-3 py-3 border-t border-slate-200 grid grid-cols-12 gap-0 text-[10px] font-black text-slate-800 text-center shrink-0" 
                  style={{ paddingRight: 'calc(0.75rem + 10px)' }}> 
                 <div className="col-span-2 text-left">TOTAL</div>
                 <div className="col-span-3">₩{formatCompact(g2bSummary.regionStats.reduce((acc, curr) => acc + curr.budget, 0))}</div>
                 <div className="col-span-3 text-blue-700 font-black">₩{formatCompact(g2bSummary.ourWinAmount)}</div>
                 <div className="col-span-2 text-rose-600">₩{formatCompact(g2bSummary.regionStats.reduce((acc, curr) => acc + curr.lost, 0))}</div>
                 <div className="col-span-2 text-right text-blue-700 font-black pr-2">
                    { (g2bSummary.ourWinAmount + g2bSummary.regionStats.reduce((acc, curr) => acc + curr.lost, 0)) > 0 
                    ? ((g2bSummary.ourWinAmount / (g2bSummary.ourWinAmount + g2bSummary.regionStats.reduce((acc, curr) => acc + curr.lost, 0))) * 100).toFixed(0) 
                    : 0 }%
                 </div>
             </div>
           </div>
        </div>

      </div>

      {/* ========================================================================= */}
      {/* 🚨 팝업 모달: 월별 매출 파이프라인 (기존 중단 디자인 100% 보존) */}
      {/* ========================================================================= */}
      {showPipelineModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white rounded-[32px] shadow-2xl w-full max-w-5xl h-[600px] flex flex-col p-6 relative">
            <div className="flex justify-between items-center mb-6 shrink-0">
              <h4 className="text-lg font-black text-slate-800 flex items-center gap-2">
                <TrendingUp size={20} className="text-blue-500"/> 
                {chartMode === 'monthly' ? '2026 월별 매출 파이프라인' : '2026 월별 품목별 매출 파이프라인'}
              </h4>
              <div className="flex gap-4 items-center">
                <div className="flex gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200">
                  <button onClick={() => setChartMode('monthly')} className={`px-4 py-1.5 rounded-lg text-xs font-black transition-all ${chartMode === 'monthly' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>월별</button>
                  <button onClick={() => setChartMode('category')} className={`px-4 py-1.5 rounded-lg text-xs font-black transition-all ${chartMode === 'category' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>품목별</button>
                </div>
                <button onClick={() => setShowPipelineModal(false)} className="p-2 bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-800 rounded-full transition-all">
                  <X size={20} strokeWidth={3} />
                </button>
              </div>
            </div>

            <div className="flex-1 min-h-0">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts.pipeline} stackOffset="sign">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="Month" axisLine={false} tickLine={false} tick={{fontSize: 12, fontWeight: 700, fill: '#64748b'}} />
                  <YAxis axisLine={false} tickLine={false} tick={{fontSize: 12, fontWeight: 700, fill: '#94a3b8'}} />
                  <Tooltip cursor={false} formatter={(v: any, name: any) => [`₩${Number(v).toLocaleString()}`, name]} />
                  <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px' }} content={(props: any) => {
                      const order = chartMode === 'monthly' ? ['실적', '확정', '예정', '미정'] : categoryKeys;
                      const colorMap: Record<string, string> = { 실적: '#3b82f6', 확정: '#10b981', 예정: '#f59e0b', 미정: '#cbd5e1' };
                      const items = order.filter((name) => (props?.payload || []).some((p: any) => p?.value === name));
                      return (<div className="flex flex-wrap justify-center gap-6 text-xs font-black text-slate-600">{items.map((name, idx) => (<span key={name} className="inline-flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-full" style={{ background: chartMode === 'monthly' ? colorMap[name] : COLORS[idx % COLORS.length] }} />{name}</span>))}</div>);
                  }} />
                  {chartMode === 'monthly' ? (
                    <><Bar name="실적" dataKey="실적" stackId="a" fill="#3b82f6" /><Bar name="확정" dataKey="확정" stackId="a" fill="#10b981" /><Bar name="예정" dataKey="예정" stackId="a" fill="#f59e0b" /><Bar name="미정" dataKey="미정" stackId="a" fill="#cbd5e1" radius={[4, 4, 0, 0]} /></>
                  ) : (
                    categoryKeys.map((key, idx) => (<Bar key={key} name={key} dataKey={key} stackId="a" fill={COLORS[idx % COLORS.length]} />))
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Dashboard;